import { useCartStore } from '@global-store/stores/cart'
import { useCheckoutResponseStore } from '@global-store/stores/checkout'
import { sprykerApiClient } from '@internal/api-client'
import type { AddToCartOptions } from '@internal/global-actions/types'
import { sleep } from 'internal/helper'

import {
  type IncreaseCartItemEvent,
  triggerIncreaseCartItemEvent,
  triggerShowAddToCartSidePanelEvent,
} from '@fond-of/event'

export const addToCart = async (
  sku: string,
  quantity = 1,
  options: Partial<AddToCartOptions> = {}
): Promise<IncreaseCartItemEvent> => {
  const cartStore = useCartStore()
  const checkoutResponseStore = useCheckoutResponseStore()

  if (cartStore.isLoading) {
    // Wait for cart is loaded
    await sleep(200)
    return await addToCart(sku, quantity, options)
  }

  const newCart = await sprykerApiClient.addToCart(
    cartStore.cart?.id || '',
    {
      quantity,
      sku,
      idPromotionalItem: options.idPromotionalItem,
    },
    {
      searchParams: {
        include:
          'gift-cards,concrete-products,concrete-product-prices,concrete-product-image-sets,promotional-items',
      },
    }
  )

  const cartItem = newCart.cartItems.find((cartItem) => cartItem.sku === sku)

  if (!cartItem) {
    throw 'Could not add item to cart'
  }

  const increaseCartItemEvent = {
    cart: newCart,
    quantity: 1,
    cartItem,
    added: !cartStore.cart?.cartItems.some((cartItem) => cartItem.sku === sku),
  }

  await options.beforeTriggerIncreaseCartItemEvent?.(increaseCartItemEvent)

  triggerIncreaseCartItemEvent(increaseCartItemEvent)

  const checkoutData =
    await checkoutResponseStore.updateCheckoutResponse(newCart)

  await cartStore.setCart(checkoutData?.carts[0])

  if (options.showSidePanel) {
    triggerShowAddToCartSidePanelEvent({
      cart: newCart,
      cartItem,
    })
  }

  return increaseCartItemEvent
}

export const cartActions = {
  addToCart,
}
